import { BASE_API_URL } from '@/constant/api'
import { AppResp } from '@/types/api'
import { axiosAuth } from '@/utils/axios'

export enum UploadScene {
  USER_AVATAR = 'user_avatar',
  USER_BACKGROUND = 'user_background',
  FEEDBACK_IMAGE = 'feedback_image',
  FEEDBACK_VIDEO = 'feedback_video',
  REPORT_IMAGE = 'report_image',
  CHARACTER_AVATAR = 'character_avatar',
  CHARACTER_IMAGE_PROMPT = 'character_image_prompt',
  CHARACTER_GREETING_VIDEO = 'character_greeting_video',
  UPLOADS = 'uploads',
  CHAT_BACKGROUND = 'chat_background',
  CHARACTER_IMAGE = 'character_image',
  SYSTEM_MESSAGE = 'system_message',
  USER_MESSAGE = 'user_message',
  ROLE_CARD = 'role_card',
  STORY_IMAGE = 'story_image',
  STORY_USER_IMAGE = 'story_user_image',
  ACTIVITY = 'activity',
}

type UploadSceneInput = UploadScene | string

type PresignUploadReq = {
  scene: UploadScene
  filename: string
  content_type: string
  file_size: number
}

type PresignUploadData = {
  upload_url: string
  object_key: string
  cdn_url: string
  expires_at: string
  storage_type: 'oss' | 'r2' | string
}

type UploadFileOptions = {
  file: Blob | File
  scene: UploadSceneInput
  filename?: string
}

const MIME_EXT_MAP: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
}

const SUPPORTED_SCENES = new Set<string>(Object.values(UploadScene))

const normalizeScene = (scene: UploadSceneInput): UploadScene => {
  if (SUPPORTED_SCENES.has(scene)) {
    return scene as UploadScene
  }
  return UploadScene.UPLOADS
}

const getContentType = (file: Blob | File): string => {
  if (file.type && file.type.trim()) return file.type
  return 'application/octet-stream'
}

const getFilename = (
  file: Blob | File,
  contentType: string,
  fallbackPrefix: string
): string => {
  if ('name' in file && file.name) return file.name
  const ext = MIME_EXT_MAP[contentType] || 'bin'
  return `${fallbackPrefix}.${ext}`
}

const presignUpload = async (req: PresignUploadReq) => {
  const res = await axiosAuth.post<AppResp<PresignUploadData>>(
    `${BASE_API_URL}/upload/presign`,
    req
  )
  if (!res.data.data) {
    throw new Error(res.data.msg || 'Failed to presign upload')
  }
  return res.data.data
}

const putToObjectStorage = async ({
  uploadUrl,
  contentType,
  file,
}: {
  uploadUrl: string
  contentType: string
  file: Blob | File
}) => {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
  })

  if (!res.ok) {
    throw new Error(`Failed to upload file: ${res.status}`)
  }
}

export const uploadFile = async ({
  file,
  scene,
  filename,
}: UploadFileOptions) => {
  const normalizedScene = normalizeScene(scene)
  const contentType = getContentType(file)
  const finalFilename =
    filename || getFilename(file, contentType, normalizedScene)

  const presignData = await presignUpload({
    scene: normalizedScene,
    filename: finalFilename,
    content_type: contentType,
    file_size: file.size,
  })

  await putToObjectStorage({
    uploadUrl: presignData.upload_url,
    contentType,
    file,
  })

  return {
    key: presignData.object_key,
    url: presignData.cdn_url,
  }
}
